import { DOCUMENT } from '@angular/common'
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core'
import {
  pickerColumnHeader,
  pickerColumnWidths,
  ValuePickerRow,
} from './value-picker'

// The header rule lives beside the geometry that sizes the panel by it; re-exported for the callers
// that have always imported it from here.
export { pickerColumnHeader }

/** The anchored value panel both transaction dialogs open — the prototype's `openEnum` popover. */
@Component({
  selector: 'hs-value-picker',
  templateUrl: './value-picker.component.html',
  styleUrls: ['./value-picker.component.scss'],
})
export class ValuePickerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() rows: readonly ValuePickerRow[] = []

  /**
   * The list's `displayColumns` (PVAL-26685). More than one makes the panel a small table: a header
   * row of column names, and one cell per column on every row. One (or none) keeps the single-line
   * value rows every static enum has always had.
   */
  @Input() columns: readonly string[] = []

  /** The value pressed — `''` for the Clear row, which is a pick like any other. */
  @Output() picked = new EventEmitter<string>()

  /**
   * Each column's width in px, from the same estimate that sized the panel, so the header and every
   * row lay out on the same tracks and a column is as wide as its own data rather than an equal
   * share of the cell. Empty for a single column.
   */
  widths: readonly number[] = []

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  /** Whether the panel is a table. Rendered even with no rows, so a search shows what it will offer. */
  get multiColumn(): boolean {
    return this.columns.length > 1
  }

  get headers(): string[] {
    return this.columns.map(pickerColumnHeader)
  }

  /**
   * Moves the panel onto `body`, the way `container="body"` does for the calendar.
   *
   * A dialog clips whatever hangs outside it — `.modal-dialog-scrollable .modal-content` is `overflow:
   * hidden` — so a panel left inside one was cut off at the dialog's edge whenever its cell sat near
   * the bottom or the right of a small screen. On `body` nothing clips it, and `:host` is `position:
   * fixed`, placed in viewport coordinates by `AnchoredPanel`.
   *
   * Here and not in the constructor: Angular inserts the host into its parent AFTER constructing it,
   * which would put it straight back.
   */
  ngOnInit(): void {
    this.renderer.appendChild(this.document.body, this.host.nativeElement)
  }

  ngOnChanges(): void {
    this.widths = this.multiColumn
      ? pickerColumnWidths(this.columns, this.rows)
      : []
  }

  /** A cell's flex: its width as the basis, still shrinking when the panel is clamped to the screen. */
  cellFlex(index: number): string | null {
    const width = this.widths[index]
    return width ? `1 1 ${width}px` : null
  }

  pick(row: ValuePickerRow, event: MouseEvent): void {
    event.preventDefault()
    this.picked.emit(row.value)
  }

  /**
   * Takes the panel back off `body`. Angular only detaches the topmost node of a view it destroys, so
   * when the dialog closes with a panel open the panel is no longer inside that node and would be
   * left standing on the page. The parent is looked up rather than assumed: a panel Angular has
   * already detached has none.
   */
  ngOnDestroy(): void {
    const element = this.host.nativeElement
    const parent = this.renderer.parentNode(element)
    if (parent) this.renderer.removeChild(parent, element)
  }
}
